import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  specialFactoryProducts,
  specialFactoryCategories,
  type SpecialFactoryProduct,
} from "@/mocks/specialFactories";
import RFQModal from "@/pages/solar-ev/components/RFQModal";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Message {
  id: number;
  from: "buyer" | "seller";
  text: string;
  time: string;
}

// ─── Chat Modal ───────────────────────────────────────────────────────────────
function ChatModal({ product, onClose }: { product: SpecialFactoryProduct; onClose: () => void }) {
  const [nameSet, setNameSet] = useState(false);
  const [buyerName, setBuyerName] = useState("");
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([
    { id: 1, from: "seller", text: `Hello! Welcome to ${product.seller}. I'm happy to assist you with ${product.name}. How can I help?`, time: "Now" },
    { id: 2, from: "seller", text: "We offer sample orders, custom branding, and fast worldwide shipping. Feel free to ask about pricing, MOQ, or specifications!", time: "Now" },
  ]);
  const bottomRef = useRef<HTMLDivElement>(null);

  const sellerReplies = [
    `Our minimum order for ${product.name} is ${product.minOrder} ${product.minOrderUnit}. Samples are welcome!`,
    "Yes, we can customize with your brand logo and packaging. OEM/ODM available.",
    "We ship worldwide via UPS, DHL, and FedEx. Delivery in 5–15 business days.",
    "We accept T/T, PayPal, Western Union, and Letter of Credit.",
    `Our factory is ISO 9001 certified. ${product.name} comes with full quality inspection.`,
    "We can provide product test reports, certificates of origin, and CE/FCC documentation.",
    `For orders over 50 units, we offer a 8–15% volume discount on ${product.name}.`,
    "Yes, we can arrange a factory video tour or third-party inspection before shipment.",
    "Production lead time is 7–15 days after order confirmation and deposit.",
    "We have shipped to 80+ countries. Our logistics team handles all customs documentation.",
  ];

  const sendMessage = () => {
    if (!input.trim()) return;
    const now = new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    const msg: Message = { id: Date.now(), from: "buyer", text: input.trim(), time: now };
    setMessages(prev => [...prev, msg]);
    setInput("");
    setTimeout(() => {
      const reply = sellerReplies[Math.floor(Math.random() * sellerReplies.length)];
      setMessages(prev => [...prev, { id: Date.now() + 1, from: "seller", text: reply, time: now }]);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }, 900 + Math.random() * 600);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  if (!nameSet) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl w-full max-w-sm p-8 text-center" onClick={e => e.stopPropagation()}>
          <div className="w-14 h-14 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-4">
            <i className="ri-message-3-line text-amber-500 text-2xl"></i>
          </div>
          <h3 className="font-extrabold text-gray-900 text-lg mb-1">Chat with Factory</h3>
          <p className="text-gray-500 text-sm mb-1">{product.sellerFlag} {product.seller}</p>
          <p className="text-gray-400 text-xs mb-5">{product.factoryAddress}</p>
          <input
            value={buyerName}
            onChange={e => setBuyerName(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && buyerName.trim()) setNameSet(true); }}
            placeholder="Your name"
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors mb-3"
            autoFocus
          />
          <button
            onClick={() => { if (buyerName.trim()) setNameSet(true); }}
            className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            Start Chat
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md flex flex-col" style={{ height: 560 }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 text-sm truncate">{product.sellerFlag} {product.seller}</p>
            <p className="text-xs text-gray-400 truncate">{product.factoryAddress}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="w-2 h-2 bg-green-400 rounded-full"></span>
            <span className="text-xs text-green-600 font-semibold">Online</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer shrink-0">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        {/* Product context */}
        <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-100 shrink-0">
          <p className="text-xs text-amber-700 font-semibold truncate">
            <i className="ri-shopping-bag-3-line mr-1"></i>
            Inquiring: {product.name} — ${product.price}/{product.priceUnit}
          </p>
        </div>
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
          {messages.map(msg => (
            <div key={msg.id} className={`flex ${msg.from === "buyer" ? "justify-end" : "justify-start"}`}>
              {msg.from === "seller" && (
                <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full shrink-0 mr-2 mt-0.5 text-xs">
                  {product.sellerFlag}
                </div>
              )}
              <div className={`max-w-[75%] flex flex-col gap-0.5 ${msg.from === "buyer" ? "items-end" : "items-start"}`}>
                <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${msg.from === "buyer" ? "bg-amber-400 text-white rounded-br-sm" : "bg-gray-100 text-gray-800 rounded-bl-sm"}`}>
                  {msg.text}
                </div>
                <span className="text-xs text-gray-400 px-1">{msg.time}</span>
              </div>
              {msg.from === "buyer" && (
                <div className="w-7 h-7 flex items-center justify-center bg-amber-400 rounded-full shrink-0 ml-2 mt-0.5">
                  <span className="text-xs text-white font-bold">{buyerName.charAt(0).toUpperCase()}</span>
                </div>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
        {/* Input */}
        <div className="px-5 py-4 border-t border-gray-100 shrink-0">
          <div className="flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Type a message..."
              className="flex-1 border border-gray-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim()}
              className="w-10 h-10 flex items-center justify-center bg-amber-400 hover:bg-amber-500 disabled:bg-gray-200 text-white rounded-full transition-colors cursor-pointer shrink-0"
            >
              <i className="ri-send-plane-fill text-sm"></i>
            </button>
          </div>
          <p className="text-xs text-gray-400 text-center mt-2">Press Enter to send · Typically replies within 1 hour</p>
        </div>
      </div>
    </div>
  );
}

// ─── Sample Order Modal ───────────────────────────────────────────────────────
function SampleOrderModal({ product, onClose }: { product: SpecialFactoryProduct; onClose: () => void }) {
  const [step, setStep] = useState<"details" | "shipping" | "payment" | "success">("details");
  const [qty, setQty] = useState(product.minOrder);
  const [fullName, setFullName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("United States");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [payMethod, setPayMethod] = useState<"card" | "paypal" | "tt">("card");
  const [cardNum, setCardNum] = useState("");
  const [cardExp, setCardExp] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [error, setError] = useState("");
  const [trackingNum, setTrackingNum] = useState("");
  const [receiptDone, setReceiptDone] = useState(false);

  const subtotal = parseFloat((product.price * qty).toFixed(2));
  const shipping = qty <= 5 ? 35 : qty <= 20 ? 65 : 120;
  const total = subtotal + shipping;

  const handleShippingNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName || !address || !city || !phone || !email) {
      setError("Please fill in all required fields.");
      return;
    }
    setError("");
    setStep("payment");
  };

  const handlePlaceOrder = (e: React.FormEvent) => {
    e.preventDefault();
    if (payMethod === "card" && (!cardNum || !cardExp || !cardCvv)) {
      setError("Please enter your card details.");
      return;
    }
    setError("");
    const tracking = `CG${Math.random().toString(36).substring(2, 8).toUpperCase()}${Math.floor(Math.random() * 9000 + 1000)}`;
    setTrackingNum(tracking);
    setStep("success");
  };

  const steps = ["details", "shipping", "payment"];
  const stepIdx = steps.indexOf(step);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            {step !== "details" && step !== "success" && (
              <button
                onClick={() => setStep(step === "payment" ? "shipping" : "details")}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
              </button>
            )}
            <h2 className="font-extrabold text-gray-900 text-base">
              {step === "details" && "Sample Order"}
              {step === "shipping" && "Shipping Info"}
              {step === "payment" && "Payment"}
              {step === "success" && "Order Confirmed!"}
            </h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Step indicator */}
        {step !== "success" && (
          <div className="flex items-center px-6 py-3 gap-2">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                  stepIdx === i ? "bg-amber-400 text-white" : stepIdx > i ? "bg-green-400 text-white" : "bg-gray-100 text-gray-400"
                }`}>
                  {stepIdx > i ? <i className="ri-check-line text-xs"></i> : i + 1}
                </div>
                <span className={`text-xs font-semibold capitalize ${stepIdx === i ? "text-amber-500" : "text-gray-400"}`}>{s}</span>
                {i < steps.length - 1 && <div className="flex-1 h-px bg-gray-200"></div>}
              </div>
            ))}
          </div>
        )}

        <div className="px-6 pb-6">
          {/* Step 1: Details */}
          {step === "details" && (
            <div className="mt-3">
              <div className="flex gap-4 mb-4">
                <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs bg-amber-50 text-amber-600 font-semibold px-2 py-0.5 rounded-full">{specialFactoryCategories.find(c => c.id === product.category)?.label}</span>
                  <h3 className="font-bold text-gray-900 text-sm mt-1.5 mb-1 line-clamp-2">{product.name}</h3>
                  <p className="text-xs text-gray-500 line-clamp-2">{product.description}</p>
                </div>
              </div>

              {/* Specs */}
              <div className="flex flex-wrap gap-1.5 mb-4">
                {product.specs.map(spec => (
                  <span key={spec} className="text-xs bg-gray-100 text-gray-600 font-semibold px-2.5 py-1 rounded-full">{spec}</span>
                ))}
              </div>

              {/* Factory */}
              <div className="bg-amber-50 rounded-xl p-3 mb-4">
                <div className="flex items-start gap-2">
                  <i className="ri-map-pin-line text-amber-500 mt-0.5 shrink-0"></i>
                  <div>
                    <p className="text-xs font-bold text-amber-700">{product.sellerFlag} {product.seller}</p>
                    <p className="text-xs text-amber-600">{product.factoryAddress}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Ships from {product.sellerCountry} → Worldwide via UPS/DHL</p>
                  </div>
                </div>
              </div>

              {/* Quantity */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="text-sm font-semibold text-gray-700">Quantity</span>
                  <p className="text-xs text-gray-400">Min. order: {product.minOrder} {product.minOrderUnit}</p>
                </div>
                <div className="flex items-center border border-gray-200 rounded-full overflow-hidden">
                  <button onClick={() => setQty(Math.max(product.minOrder, qty - 1))} className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer text-lg">-</button>
                  <span className="px-5 text-sm font-bold text-gray-800">{qty}</span>
                  <button onClick={() => setQty(Math.min(product.stock, qty + 1))} className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer text-lg">+</button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Unit Price</span>
                  <span className="font-semibold">${product.price}/{product.priceUnit}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Subtotal ({qty} {product.minOrderUnit})</span>
                  <span className="font-semibold">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-400 mb-3">
                  <span>International Shipping</span>
                  <span>Calculated next step</span>
                </div>
                <div className="border-t border-gray-200 pt-3 flex justify-between">
                  <span className="font-bold text-gray-800">Est. Total</span>
                  <span className="font-extrabold text-amber-500 text-lg">${subtotal.toFixed(2)}+</span>
                </div>
              </div>

              <button
                onClick={() => setStep("shipping")}
                className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                Continue to Shipping <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          )}

          {/* Step 2: Shipping */}
          {step === "shipping" && (
            <form onSubmit={handleShippingNext} className="mt-3 flex flex-col gap-3.5">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Full Name *</label>
                <input required value={fullName} onChange={e => setFullName(e.target.value)} placeholder="John Smith" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Email *</label>
                <input required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="john@company.com" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Street Address *</label>
                <input required value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Main Street, Suite 100" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">City *</label>
                  <input required value={city} onChange={e => setCity(e.target.value)} placeholder="Los Angeles" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Country *</label>
                  <input required value={country} onChange={e => setCountry(e.target.value)} placeholder="United States" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Phone *</label>
                <input required type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 000-0000" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-truck-line text-amber-500"></i>
                  <span className="text-sm font-bold text-amber-700">Shipping Estimate</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">UPS/DHL International</span>
                  <span className="font-bold text-amber-600">${shipping}</span>
                </div>
                <p className="text-xs text-gray-500">Estimated delivery: 7–15 business days worldwide</p>
              </div>
              {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
              <button type="submit" className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                Continue to Payment <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </form>
          )}

          {/* Step 3: Payment */}
          {step === "payment" && (
            <form onSubmit={handlePlaceOrder} className="mt-3">
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Order Summary</h4>
                <div className="flex gap-3 mb-3">
                  <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 line-clamp-2">{product.name}</p>
                    <p className="text-xs text-gray-400">Qty: {qty} × ${product.price}</p>
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-3 flex flex-col gap-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Subtotal</span>
                    <span className="font-semibold">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">International Shipping</span>
                    <span className="font-semibold">${shipping}</span>
                  </div>
                  <div className="flex justify-between text-sm font-bold text-gray-900 pt-1 border-t border-gray-200 mt-1">
                    <span>Total</span>
                    <span className="text-amber-500 text-base">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Deliver To</h4>
                <p className="text-sm font-semibold text-gray-800">{fullName}</p>
                <p className="text-sm text-gray-600">{address}, {city}, {country}</p>
                <p className="text-sm text-gray-500">{phone}</p>
              </div>

              <div className="mb-4">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Payment Method</h4>
                <div className="flex gap-2 mb-3">
                  {[
                    { key: "card", icon: "ri-bank-card-line", label: "Credit Card" },
                    { key: "paypal", icon: "ri-paypal-line", label: "PayPal" },
                    { key: "tt", icon: "ri-bank-line", label: "T/T Wire" },
                  ].map(pm => (
                    <button
                      key={pm.key}
                      type="button"
                      onClick={() => setPayMethod(pm.key as typeof payMethod)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border-2 text-xs font-semibold cursor-pointer transition-all ${payMethod === pm.key ? "border-amber-400 bg-amber-50 text-amber-600" : "border-gray-200 text-gray-500"}`}
                    >
                      <i className={pm.icon}></i> {pm.label}
                    </button>
                  ))}
                </div>
                {payMethod === "card" && (
                  <div className="flex flex-col gap-3">
                    <input value={cardNum} onChange={e => setCardNum(e.target.value)} placeholder="Card number" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" maxLength={19} />
                    <div className="grid grid-cols-2 gap-3">
                      <input value={cardExp} onChange={e => setCardExp(e.target.value)} placeholder="MM/YY" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" maxLength={5} />
                      <input value={cardCvv} onChange={e => setCardCvv(e.target.value)} placeholder="CVV" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" maxLength={4} />
                    </div>
                  </div>
                )}
                {payMethod === "paypal" && (
                  <div className="bg-amber-50 rounded-xl p-4 text-center">
                    <i className="ri-paypal-line text-3xl text-amber-500 mb-2 block"></i>
                    <p className="text-sm text-gray-600">You will be redirected to PayPal to complete payment.</p>
                  </div>
                )}
                {payMethod === "tt" && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs font-bold text-gray-600 mb-2">Bank Wire Transfer (T/T)</p>
                    <div className="flex flex-col gap-1 text-xs text-gray-500">
                      <div className="flex justify-between"><span>Bank:</span><span className="font-semibold text-gray-700">HSBC Hong Kong</span></div>
                      <div className="flex justify-between"><span>Account:</span><span className="font-semibold text-gray-700">CrownGlobal Trade Ltd</span></div>
                      <div className="flex justify-between"><span>SWIFT:</span><span className="font-semibold text-gray-700">HSBCHKHHHKH</span></div>
                      <div className="flex justify-between"><span>Amount:</span><span className="font-semibold text-amber-600">${total.toFixed(2)}</span></div>
                    </div>
                  </div>
                )}
              </div>

              {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg mb-3">{error}</p>}

              <button type="submit" className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-base">
                <i className="ri-lock-line mr-2"></i>Place Sample Order — ${total.toFixed(2)}
              </button>
              <p className="text-center text-xs text-gray-400 mt-2">SSL secured · Payment held until you confirm receipt</p>
            </form>
          )}

          {/* Success */}
          {step === "success" && (
            <div className="flex flex-col items-center py-6 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mb-4">
                <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
              </div>
              <h3 className="text-xl font-extrabold text-gray-900 mb-2">Sample Order Placed!</h3>
              <p className="text-gray-500 text-sm mb-5">Your order is confirmed. The factory will ship soon.</p>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 w-full mb-4 text-left">
                <p className="text-xs text-amber-600 font-semibold mb-1">Tracking Number</p>
                <p className="text-base font-extrabold text-gray-900 font-mono tracking-wider">{trackingNum}</p>
                <p className="text-xs text-gray-400 mt-1">Track via UPS/DHL · Delivery: 7–15 business days</p>
              </div>

              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 w-full mb-4 text-left">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-shield-check-line text-sky-500"></i>
                  <span className="text-sm font-bold text-sky-700">Payment Protected</span>
                </div>
                <p className="text-xs text-sky-600 leading-relaxed">
                  Your payment of <strong>${total.toFixed(2)}</strong> is securely held and will only be released to the factory after you confirm receipt.
                </p>
              </div>

              {!receiptDone ? (
                <div className="w-full bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-4">
                  <p className="text-xs font-bold text-green-700 mb-1">Received your order?</p>
                  <p className="text-xs text-green-600 mb-3">Click below to confirm receipt and release payment to the factory.</p>
                  <button
                    onClick={() => setReceiptDone(true)}
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-checkbox-circle-line mr-2"></i>Confirm Receipt &amp; Release Payment
                  </button>
                </div>
              ) : (
                <div className="w-full bg-green-50 border border-green-200 rounded-xl p-4 mb-4 text-center">
                  <i className="ri-hand-heart-line text-green-500 text-2xl mb-1 block"></i>
                  <p className="text-sm font-bold text-green-700">Payment Released to Factory!</p>
                  <p className="text-xs text-green-600 mt-1">Thank you for trading on CrownGlobal!</p>
                </div>
              )}

              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm cursor-pointer whitespace-nowrap">
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Product Card ─────────────────────────────────────────────────────────────
function FactoryProductCard({
  product,
  onOrder,
  onChat,
}: {
  product: SpecialFactoryProduct;
  onOrder: () => void;
  onChat: () => void;
}) {
  const cat = specialFactoryCategories.find(c => c.id === product.category);
  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden group hover:-translate-y-1 transition-transform duration-300 flex flex-col">
      <div className="relative w-full h-44 overflow-hidden cursor-pointer" onClick={onOrder}>
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-3 flex flex-col flex-1">
        {cat && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border w-fit mb-2 ${cat.color}`}>
            <i className={`${cat.icon} mr-1`}></i>{cat.label}
          </span>
        )}
        <h3
          className="font-semibold text-gray-800 text-sm mb-1 line-clamp-2 cursor-pointer hover:text-amber-500 transition-colors flex-1"
          onClick={onOrder}
        >
          {product.name}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-1 mb-1.5">
          {[1,2,3,4,5].map(s => (
            <i key={s} className={`${s <= Math.round(product.rating) ? "ri-star-fill" : "ri-star-line"} text-amber-400 text-xs`}></i>
          ))}
          <span className="text-xs text-gray-400 ml-0.5">({product.reviews.toLocaleString()})</span>
        </div>

        {/* Factory */}
        <div className="flex items-start gap-1 mb-2">
          <i className="ri-map-pin-line text-amber-400 text-xs mt-0.5 shrink-0"></i>
          <span className="text-xs text-gray-400 line-clamp-1">{product.factoryAddress}</span>
        </div>

        {/* Specs */}
        <div className="flex flex-wrap gap-1 mb-2">
          {product.specs.slice(0, 2).map(spec => (
            <span key={spec} className="text-xs bg-gray-50 text-gray-500 px-1.5 py-0.5 rounded-md border border-gray-100">{spec}</span>
          ))}
        </div>

        <div className="flex items-center justify-between mb-1">
          <span className="text-amber-500 font-extrabold text-lg">${product.price}<span className="text-xs font-normal text-gray-400">/{product.priceUnit}</span></span>
          <span className="text-xs text-gray-400">{product.sold.toLocaleString()} sold</span>
        </div>
        <p className="text-xs text-gray-400 mb-3">Min. order: {product.minOrder} {product.minOrderUnit}</p>

        <div className="flex gap-1.5 mt-auto">
          <button
            onClick={onOrder}
            className="flex-1 bg-amber-400 hover:bg-amber-500 text-white text-xs font-bold py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-shopping-cart-line mr-1"></i>Sample Order
          </button>
          <button
            onClick={onChat}
            className="w-8 h-8 flex items-center justify-center border border-gray-200 hover:border-amber-300 text-gray-500 hover:text-amber-500 rounded-full transition-colors cursor-pointer shrink-0"
            title="Chat with factory"
          >
            <i className="ri-message-3-line text-xs"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Section ─────────────────────────────────────────────────────────────
export default function SpecialFactorySection() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("all");
  const [orderProduct, setOrderProduct] = useState<SpecialFactoryProduct | null>(null);
  const [chatProduct, setChatProduct] = useState<SpecialFactoryProduct | null>(null);
  const [showRFQ, setShowRFQ] = useState(false);

  const allCats = [{ id: "all", label: "All Categories", icon: "ri-apps-line", color: "bg-gray-100 text-gray-700 border-gray-200" }, ...specialFactoryCategories];

  const filtered = activeCategory === "all"
    ? specialFactoryProducts
    : specialFactoryProducts.filter(p => p.category === activeCategory);

  return (
    <section className="py-16 bg-white">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8">

        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-1 h-6 bg-green-500 rounded-full inline-block"></span>
              <p className="text-green-600 text-sm font-bold uppercase tracking-widest">Verified Factory Direct</p>
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-2">
              Green Energy, EV, Hardware &amp; Sport Factory Hub
            </h2>
            <p className="text-gray-500 text-sm max-w-2xl">
              Source directly from top-verified factories — Solar panels, batteries, inverters, golf carts, motorcycles, e-bikes, brass hose nozzles, faucets, welding cables, sport clothes &amp; garden tools. Chat with sellers instantly and place sample orders today.
            </p>
            {/* Trust badges */}
            <div className="flex flex-wrap gap-2 mt-4">
              {[
                { icon: "ri-shield-check-line", label: "Verified Factories", color: "text-green-600 bg-green-50 border-green-200" },
                { icon: "ri-message-3-line", label: "Instant Chat", color: "text-amber-600 bg-amber-50 border-amber-200" },
                { icon: "ri-shopping-cart-line", label: "Sample Orders", color: "text-sky-600 bg-sky-50 border-sky-200" },
                { icon: "ri-truck-line", label: "Worldwide Shipping", color: "text-rose-600 bg-rose-50 border-rose-200" },
              ].map(b => (
                <span key={b.label} className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${b.color}`}>
                  <i className={b.icon}></i>{b.label}
                </span>
              ))}
            </div>
          </div>

          {/* Stats + RFQ button */}
          <div className="flex flex-col gap-3 shrink-0">
            <button
              onClick={() => setShowRFQ(true)}
              className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-list-3-line"></i> Request Bulk Quote (RFQ)
            </button>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: "500+", label: "Verified Factories", icon: "ri-building-2-line" },
                { value: "11 Categories", label: "Product Types", icon: "ri-apps-line" },
                { value: "80+ Countries", label: "Shipping Reach", icon: "ri-global-line" },
                { value: "24/7", label: "Seller Support", icon: "ri-customer-service-2-line" },
              ].map(s => (
                <div key={s.label} className="bg-gray-50 rounded-xl p-3 text-center">
                  <i className={`${s.icon} text-amber-400 text-lg block mb-1`}></i>
                  <p className="font-extrabold text-gray-900 text-sm">{s.value}</p>
                  <p className="text-xs text-gray-400">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Hero Banner */}
        <div className="relative rounded-2xl overflow-hidden mb-8">
          <img
            src="https://public.readdy.ai/ai/img_res/5215fadf4e2ed0b99ac636750c1b9231.jpg"
            alt="Special Factory Hub"
            className="w-full h-40 md:h-52 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/50 to-transparent"></div>
          <div className="absolute inset-0 flex items-center px-6 md:px-12">
            <div>
              <h3 className="text-white text-xl md:text-2xl font-extrabold mb-2">Factory-to-Door. No Middlemen.</h3>
              <p className="text-white/80 text-sm max-w-lg">
                Buy solar panels, EV vehicles, sport apparel &amp; garden tools directly from certified factories. Sample orders start from 1 unit. Chat with the factory before you buy.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {["Solar Energy", "Golf Carts", "E-Bikes", "Brass Nozzles", "Faucets", "Welding Cables", "Sport Clothes", "Garden Tools"].map(tag => (
                  <span key={tag} className="text-xs bg-white/20 text-white font-semibold px-3 py-1 rounded-full border border-white/30">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 flex-wrap mb-6">
          {allCats.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat.id
                  ? "bg-amber-400 text-white border-amber-400"
                  : `${cat.color} hover:border-amber-300`
              }`}
            >
              <i className={cat.icon}></i>{cat.label}
            </button>
          ))}
        </div>

        <p className="text-xs text-gray-400 mb-4">{filtered.length} products from verified factories</p>

        {/* Product Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-10" data-product-shop>
          {filtered.map(p => (
            <FactoryProductCard
              key={p.id}
              product={p}
              onOrder={() => setOrderProduct(p)}
              onChat={() => setChatProduct(p)}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="bg-gradient-to-r from-amber-50 to-green-50 border border-amber-100 rounded-2xl p-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-extrabold text-gray-900 mb-2">Explore the Full Solar &amp; EV Factory Hub</h3>
              <p className="text-gray-500 text-sm max-w-lg">
                View detailed factory profiles with certifications (ISO, CE, SGS), production capacity, years in business, and full product catalogs. Submit one RFQ to 500+ factories at once.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 shrink-0">
              <button
                onClick={() => navigate("/solar-ev")}
                className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-8 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-sun-line"></i> Solar &amp; EV Factory Hub
              </button>
              <button
                onClick={() => setShowRFQ(true)}
                className="flex items-center gap-2 border border-green-400 text-green-600 hover:bg-green-50 font-bold px-8 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-list-3-line"></i> Send Bulk RFQ
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {orderProduct && (
        <SampleOrderModal product={orderProduct} onClose={() => setOrderProduct(null)} />
      )}
      {chatProduct && (
        <ChatModal product={chatProduct} onClose={() => setChatProduct(null)} />
      )}
      {showRFQ && (
        <RFQModal preselectedFactory={null} onClose={() => setShowRFQ(false)} />
      )}
    </section>
  );
}
